<template>
    <div class="qr-receipt-se-card mb-3">
        <!-- Header Start -->
        <div class="receipt-toolbar">
            <h3 class="receipt-toolbar-title">QR mendatang</h3>

            <div class="receipt-toolbar-filters">
                <!-- Date range picker -->
                <div class="date-picker-wrapper">
                    <i class="fas fa-calendar-alt calendar-icon"></i>
                    <flat-pickr v-model="dateValue" :config="dateRangeConfig"></flat-pickr>
                </div>

                <!-- Dropdown Status -->
                <div class="receipt-filter">
                    <MultiSelect
                        v-model="filter.status"
                        :options="STATUS_OPTIONS"
                        :custom-label="formatStatusLabel"
                        class="qr-receipt-select"
                        :class="{ 'qr-receipt-select--active': !!filter.status }"
                        placeholder="Semua Status"
                        :searchable="false"
                        select-label=""
                        deselect-label=""
                        selected-label=""
                        :allow-empty="true"
                    ></MultiSelect>
                </div>
            </div>
        </div>

        <!-- Tabs Alur Penerimaan -->
        <div class="flow-tabs" role="tablist">
            <button
                v-for="tab in tabs"
                :key="tab.value"
                type="button"
                role="tab"
                class="flow-tab"
                :class="{ 'flow-tab--active': flow === tab.value }"
                :aria-selected="flow === tab.value ? 'true' : 'false'"
                @click="handleTabChange(tab.value)"
            >
                {{ tab.label }}
            </button>
        </div>
        <!-- Header End -->

        <!-- Table Start (Scrollable Left-Right) -->
        <div class="w-100 overflow-auto table-scroll-container">
            <table class="table w-100 qtrust-table" style="min-width: 1100px;">
                <thead>
                    <tr>
                        <th v-for="column in columns" :key="column.key">
                            {{ column.label }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.id">
                        <td v-for="column in columns" :key="column.key">
                            <template v-if="column.key === 'receivedAt'">
                                <div class="received-cell">
                                    <span>{{ formatCell(row, column) }}</span>
                                    <button
                                        v-if="row.status === 'RECEIVED'"
                                        type="button"
                                        class="btn-bast"
                                        data-tooltip="Download BAST"
                                        aria-label="Download BAST"
                                        @click="handlePreviewBast(row)"
                                    >
                                        <i class="fas fa-file-download"></i>
                                    </button>
                                </div>
                            </template>
                            <template v-else>{{ formatCell(row, column) }}</template>
                        </td>
                    </tr>
                    <tr v-if="!isLoading && rows.length === 0">
                        <td :colspan="columns.length" class="text-center text-muted py-4">
                            {{ hasError ? 'Gagal memuat data penerimaan' : 'Belum ada penerimaan pada filter ini' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- Table End -->

        <div class="d-flex justify-content-end align-items-center py-2 px-3" v-if="rows.length">
            <p class="text-muted mb-0" style="font-size: 13px;">Showing {{ (config.page-1)*config.limit+1 }}-{{ (config.page-1)*config.limit+rows.length }} of {{ config.total }} Items</p>
            <div class="d-flex align-items-center text-muted ml-3">
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(1)"><i class="fa fa-angle-double-left"></i></button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(config.page - 1)"><i class="fa fa-angle-left"></i></button>
                <p class="px-2 text-dark mb-0" style="font-size: 13px;">{{ config.page }}</p>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= config.lastPage" @click="goToPage(config.page + 1)"><i class="fa fa-angle-right"></i></button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= config.lastPage" @click="goToPage(config.lastPage)"><i class="fa fa-angle-double-right"></i></button>
            </div>
        </div>

        <!-- Preview BAST (mounted only while open) -->
        <bast-preview-modal
            v-if="bastPreview"
            :issue-id="bastPreview.issueId"
            :bast-no="bastPreview.bastNo"
            :qty="bastPreview.qty"
            @close="bastPreview = null"
        />
    </div>
</template>

<script>
    import moment from 'moment';
    import BastPreviewModal from '../../QRIssue/children/BastPreviewModal.vue';
    import {
        FLOW_DEALER_POS,
        FLOW_MD_DEALER,
        formatCount,
    } from '@/services/qr-issue';
    import { fetchReceiptPage } from '@/services/qr-receipt';

    const STATUS_OPTIONS = ['INTRANSIT', 'RECEIVED'];
    const DATE_TIME_FORMAT = 'DD MMM YYYY | HH:mm';
    const API_DATE_FORMAT = 'YYYY-MM-DD';
    const RANGE_SEPARATOR = ' s/d ';

    const POS_COLUMNS = [
        { key: 'posCode', label: 'Kode POS' },
        { key: 'posName', label: 'Nama POS' },
    ];

    function buildColumns(flow) {
        return [
            { key: 'bastNo', label: 'Kode BAST' },
            { key: 'dealerCode', label: 'Kode Dealer' },
            { key: 'dealerName', label: 'Nama Dealer' },
            ...(flow === FLOW_DEALER_POS ? POS_COLUMNS : []),
            { key: 'qty', label: 'Jumlah', type: 'count' },
            { key: 'deliveredAt', label: 'Tanggal Pengiriman', type: 'datetime' },
            { key: 'status', label: 'Status', type: 'status' },
            { key: 'receivedAt', label: 'Tanggal Penerimaan', type: 'datetime' },
        ];
    }

    export default {
        components : {
            BastPreviewModal,
        },
        data () {
            // Default period: first day of the current month through today.
            const start = moment().startOf('month').format(API_DATE_FORMAT);
            const end = moment().format(API_DATE_FORMAT);
            return {
                STATUS_OPTIONS,
                tabs : [
                    { value: FLOW_MD_DEALER, label: 'MD → Dealer' },
                    { value: FLOW_DEALER_POS, label: 'Dealer → POS' },
                ],
                flow : FLOW_MD_DEALER,
                startDate : start,
                endDate : end,
                dateValue : `${start}${RANGE_SEPARATOR}${end}`,
                dateRangeConfig : {
                    mode: 'range',
                    altFormat: 'd-m-Y',
                    dateFormat: 'Y-m-d',
                    altInput: true,
                    altInputClass: 'qr-receipt-flatpickr-alt',
                    locale: { rangeSeparator: RANGE_SEPARATOR },
                    onClose: this.onCloseDate,
                },
                rows : [],
                isLoading : false,
                hasError : false,
                requestId : 0,
                bastPreview : null,
                filter : {
                    status : null,
                },
                config : {
                    page: 1,
                    limit: 15,
                    lastPage: 0,
                    total: 0,
                    sort_by: 'delivery_at',
                    sort_order: 'DESC',
                },
            }
        },
        computed : {
            columns () {
                return buildColumns(this.flow);
            },
            query () {
                return {
                    flow: this.flow,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    status: this.filter.status,
                };
            },
            queryKey () {
                return JSON.stringify(this.query);
            },
        },
        watch : {
            queryKey () {
                // The page watcher refetches when the page actually changes.
                if (this.config.page !== 1) {
                    this.config.page = 1;
                    return;
                }
                this.generateDatas();
            },
            'config.page' : 'generateDatas',
        },
        mounted () {
            this.$emit('flow-change', this.flow);
            this.generateDatas();
        },
        methods : {
            formatStatusLabel (status) {
                if (status === 'INTRANSIT') return 'In-Transit';
                if (status === 'RECEIVED') return 'Received';
                return status;
            },
            formatCell (row, column) {
                const value = row[column.key];
                if (column.type === 'datetime') return value ? moment(value).format(DATE_TIME_FORMAT) : '-';
                if (column.type === 'count') return formatCount(value);
                if (column.type === 'status') return value ? this.formatStatusLabel(value) : '-';
                return value || '-';
            },
            onCloseDate (dates) {
                // One selected date means a single-day range.
                if (!dates.length) return;
                const format = (date) => moment(date).format(API_DATE_FORMAT);
                this.startDate = format(dates[0]);
                this.endDate = format(dates[1] || dates[0]);
            },
            handleTabChange (flow) {
                if (this.flow === flow) return;
                // Drop rows of the previous flow so they never show under the
                // new flow's columns while the request is in flight.
                this.rows = [];
                this.flow = flow;
                this.$emit('flow-change', this.flow);
            },
            handlePreviewBast (row) {
                this.bastPreview = { issueId: row.id, bastNo: row.bastNo, qty: row.qty };
            },
            goToPage (page) {
                if (page >= 1 && page <= this.config.lastPage) this.config.page = page;
            },
            async generateDatas () {
                const requestId = ++this.requestId;
                this.isLoading = true;
                this.hasError = false;
                try {
                    const page = await fetchReceiptPage(this.$http, {
                        ...this.query,
                        page: this.config.page,
                        limit: this.config.limit,
                        sortBy: this.config.sort_by,
                        sortOrder: this.config.sort_order,
                    });
                    if (requestId !== this.requestId) return;
                    this.rows = page.rows;
                    this.config.lastPage = page.lastPage;
                    this.config.total = page.total;
                } catch (err) {
                    console.log(err);
                    if (requestId !== this.requestId) return;
                    this.hasError = true;
                    this.rows = [];
                    this.config.lastPage = 0;
                    this.config.total = 0;
                } finally {
                    if (requestId === this.requestId) this.isLoading = false;
                }
            },
        },
    }
</script>


<style>
    /* MultiSelect and flat-pickr render their own DOM, so these cannot be scoped.
       Everything hangs off .qr-receipt-se-card so nothing leaks to other pages. */
    .qr-receipt-se-card .qr-receipt-select.multiselect {
        min-height: 0;
        font-size: 12px;
        color: #2F2F2F;
    }

    .qr-receipt-se-card .qr-receipt-select .multiselect__tags {
        display: flex;
        align-items: center;
        min-height: 32px;
        height: 32px;
        padding: 0 32px 0 12px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
    }

    .qr-receipt-se-card .qr-receipt-select .multiselect__placeholder,
    .qr-receipt-se-card .qr-receipt-select .multiselect__single,
    .qr-receipt-se-card .qr-receipt-select .multiselect__input {
        min-height: 0;
        margin: 0;
        padding: 0;
        font-size: 12px;
        line-height: 30px;
        color: #2F2F2F;
        background: transparent;
    }

    .qr-receipt-se-card .qr-receipt-select .multiselect__single {
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .qr-receipt-se-card .qr-receipt-select .multiselect__select {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 30px;
        padding: 0;
        line-height: 1;
    }

    .qr-receipt-se-card .qr-receipt-select .multiselect__select::before {
        position: static;
        z-index: 1;
        margin: 0;
        border-width: 6px 4px 0;
        border-color: #000000 transparent transparent;
    }

    .qr-receipt-se-card .qr-receipt-select .multiselect__content-wrapper {
        border-color: #F3F3F3;
        z-index: 20;
    }

    .qr-receipt-se-card .qr-receipt-select .multiselect__option {
        min-height: 0;
        padding: 9px 12px;
        font-size: 12px;
        line-height: 14px;
    }

    .qr-receipt-se-card .qr-receipt-select.qr-receipt-select--active .multiselect__tags {
        border-color: #F36F21;
    }

    .qr-receipt-se-card .qr-receipt-flatpickr-alt {
        border: none !important;
        outline: none !important;
        font-size: 12px !important;
        background: transparent !important;
        color: #2F2F2F !important;
        width: 162px !important;
        height: 30px !important;
        padding: 0 !important;
        cursor: pointer !important;
    }
</style>

<style scoped>
    .qr-receipt-se-card {
        background-color: #FFFFFF;
        border-radius: 8px;
    }

    /* Toolbar: title on the left, date range + status on the right */
    .receipt-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px 20px;
        padding: 20px 20px 16px 40px;
    }

    .receipt-toolbar-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .receipt-toolbar-filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px 20px;
    }

    .date-picker-wrapper {
        display: flex;
        align-items: center;
        width: 213px;
        height: 32px;
        padding: 0 16px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
    }

    .calendar-icon {
        margin-right: 9px;
        color: #2F2F2F;
        font-size: 14px;
    }

    .receipt-filter {
        width: 151px;
        white-space: nowrap;
    }

    /* Tabs */
    .flow-tabs {
        display: flex;
        gap: 24px;
        padding: 0 40px;
        border-bottom: 1px solid #F3F3F3;
    }

    .flow-tab {
        padding: 0 0 10px;
        background: transparent;
        border: none;
        border-bottom: 2px solid transparent;
        border-radius: 0;
        font-size: 12px;
        font-weight: 400;
        color: #B2B2B2;
        cursor: pointer;
    }

    .flow-tab:hover {
        color: #707070;
    }

    .flow-tab--active,
    .flow-tab--active:hover {
        font-weight: 600;
        color: #F36F21;
        border-bottom-color: #F36F21;
    }

    /* Table */
    .table-scroll-container {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .table-scroll-container::-webkit-scrollbar {
        height: 4px;
    }

    .table-scroll-container::-webkit-scrollbar-track {
        background: transparent;
    }

    .table-scroll-container::-webkit-scrollbar-thumb {
        background: #D9D9D9;
        border-radius: 50px;
    }

    .table-scroll-container::-webkit-scrollbar-thumb:hover {
        background: #B2B2B2;
    }

    .qtrust-table {
        margin-bottom: 0;
    }

    .qtrust-table > thead > tr > th {
        text-align: left;
        border-top: none;
        border-bottom: 1px solid #F3F3F3;
        padding: 12px 16px 12px 0;
        height: 40px;
        vertical-align: middle;
        font-size: 12px;
        font-weight: 700;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .qtrust-table > tbody > tr > td {
        text-align: left;
        padding: 12px 16px 12px 0;
        border-top: none;
        border-bottom: 1px solid #F3F3F3;
        height: 40px;
        vertical-align: middle;
        font-size: 12px;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .qtrust-table > thead > tr > th:first-child,
    .qtrust-table > tbody > tr > td:first-child {
        padding-left: 40px;
    }

    .received-cell {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    /* BAST download icon with a CSS tooltip (shown on hover and keyboard focus) */
    .btn-bast {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        background: transparent;
        border: none;
        font-size: 12px;
        line-height: 1;
        color: #000000;
        cursor: pointer;
    }

    .btn-bast:focus-visible {
        outline: 2px solid #F36F21;
        outline-offset: 2px;
        border-radius: 2px;
    }

    .btn-bast::after {
        content: attr(data-tooltip);
        position: absolute;
        right: -12px;
        bottom: calc(100% + 8px);
        z-index: 10;
        padding: 8px 12px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        font-size: 12px;
        line-height: 14px;
        color: #333333;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.15s ease;
    }

    .btn-bast:hover::after,
    .btn-bast:focus-visible::after {
        opacity: 1;
        visibility: visible;
    }
</style>
